import { useEffect } from 'react';
import './IcyWindAnimation.css';

export default function IcyWindAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle background
    const battleBg = playerEl.closest('.battle-bg') || document.querySelector('.battle-bg');
    if (!battleBg) {
      console.error('Battle background not found');
      onComplete();
      return;
    }

    // Get positions
    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentRef.current.getBoundingClientRect();
    const battleBgRect = battleBg.getBoundingClientRect();

    // Calculate positions relative to battle background
    const startX = (playerRect.left + playerRect.width / 2) - battleBgRect.left;
    const startY = (playerRect.top + playerRect.height / 2) - battleBgRect.top;
    const endX = (opponentRect.left + opponentRect.width / 2) - battleBgRect.left;
    const endY = (opponentRect.top + opponentRect.height / 2) - battleBgRect.top;

    // Track all created elements
    const elements = [];
    const timers = [];

    // Create initial frost buildup at player
    const frostBuildup = document.createElement('div');
    frostBuildup.className = 'icy-wind-buildup';
    frostBuildup.style.left = `${startX}px`;
    frostBuildup.style.top = `${startY}px`;
    battleBg.appendChild(frostBuildup);
    elements.push(frostBuildup);

    // Player animation - cold breath effect
    playerEl.style.animation = 'icyWindPlayerBreath 0.6s ease-in-out';

    // Create main icy wind storm
    const mainWindTimer = setTimeout(() => {
      // Create swirling icy wind at opponent's position
      const icyTornado = document.createElement('div');
      icyTornado.className = 'icy-wind-tornado';
      icyTornado.style.left = `${endX}px`;
      icyTornado.style.top = `${endY}px`;
      battleBg.appendChild(icyTornado);
      elements.push(icyTornado);
      
      // Create multiple ice crystal waves
      for (let i = 0; i < 5; i++) {
        const waveTimer = setTimeout(() => {
          // Create ice crystal wave that travels from player to opponent
          const iceWave = document.createElement('div');
          iceWave.className = 'icy-wind-wave';
          iceWave.style.left = `${startX}px`;
          iceWave.style.top = `${startY}px`;
          
          // Calculate direction and distance
          const dx = endX - startX;
          const dy = endY - startY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const angle = Math.atan2(dy, dx);
          
          iceWave.style.setProperty('--travel-distance', `${distance}px`);
          iceWave.style.setProperty('--travel-angle', `${angle}rad`);
          iceWave.style.setProperty('--wave-delay', `${i * 0.1}s`);
          
          battleBg.appendChild(iceWave);
          elements.push(iceWave);
          
          // Create ice crystals along the wave path
          for (let j = 0; j < 8; j++) {
            const crystalTimer = setTimeout(() => {
              const crystal = document.createElement('div');
              crystal.className = 'icy-wind-crystal';
              
              // Position along the path
              const progress = j / 8;
              const crystalX = startX + dx * progress;
              const crystalY = startY + dy * progress;
              
              // Add some randomness
              const offsetX = (Math.random() - 0.5) * 40;
              const offsetY = (Math.random() - 0.5) * 40;
              
              crystal.style.left = `${crystalX + offsetX}px`;
              crystal.style.top = `${crystalY + offsetY}px`;
              crystal.style.setProperty('--rotation', `${Math.random() * 360}deg`);
              crystal.style.setProperty('--float-direction', `${Math.random() * 360}deg`);
              
              battleBg.appendChild(crystal);
              elements.push(crystal);
              
              // Auto-remove crystal after animation
              const crystalRemoveTimer = setTimeout(() => {
                if (crystal.isConnected) {
                  crystal.remove();
                }
              }, 1200);
              
              timers.push(crystalRemoveTimer);
            }, j * 80); // Staggered crystal appearance
            
            timers.push(crystalTimer);
          }
          
          // Auto-remove wave after animation
          const waveRemoveTimer = setTimeout(() => {
            if (iceWave.isConnected) {
              iceWave.remove();
            }
          }, 1500);
          
          timers.push(waveRemoveTimer);
        }, i * 150); // Staggered wave release
        
        timers.push(waveTimer);
      }
      
      // Create frost particles around opponent
      for (let i = 0; i < 15; i++) {
        const particleTimer = setTimeout(() => {
          const particle = document.createElement('div');
          particle.className = 'icy-wind-particle';
          
          // Random position around opponent
          const angle = (i / 15) * Math.PI * 2;
          const radius = 40 + Math.random() * 30;
          const particleX = endX + Math.cos(angle) * radius;
          const particleY = endY + Math.sin(angle) * radius;
          
          particle.style.left = `${particleX}px`;
          particle.style.top = `${particleY}px`;
          particle.style.setProperty('--drift-x', `${(Math.random() - 0.5) * 60}px`);
          particle.style.setProperty('--drift-y', `${(Math.random() - 0.5) * 60}px`);
          
          battleBg.appendChild(particle);
          elements.push(particle);
          
          // Auto-remove particle after animation
          const particleRemoveTimer = setTimeout(() => {
            if (particle.isConnected) {
              particle.remove();
            }
          }, 1000);
          
          timers.push(particleRemoveTimer);
        }, i * 50); // Staggered particle release
        
        timers.push(particleTimer);
      }
    }, 500); // After buildup
    
    timers.push(mainWindTimer);

    // Hit effect and speed reduction indicator
    const hitTimer = setTimeout(() => {
      if (opponentEl) {
        // Create freeze hit effect
        const hitEffect = document.createElement('div');
        hitEffect.className = 'icy-wind-hit-effect';
        hitEffect.style.left = `${endX}px`;
        hitEffect.style.top = `${endY}px`;
        battleBg.appendChild(hitEffect);
        elements.push(hitEffect);
        
        // Create speed reduction indicator

        
        // Make opponent shiver
        opponentEl.style.animation = 'icyWindShiver 0.8s ease-in-out';
        
        // Create frost coating on opponent
        const frostCoating = document.createElement('div');
        frostCoating.className = 'icy-wind-frost-coating';
        frostCoating.style.left = `${endX}px`;
        frostCoating.style.top = `${endY}px`;
        battleBg.appendChild(frostCoating);
        elements.push(frostCoating);
        
        // Reset opponent animation
        const opponentResetTimer = setTimeout(() => {
          if (opponentEl) {
            opponentEl.style.animation = '';
          }
        }, 800);
        
        timers.push(opponentResetTimer);
      }
    }, 1000); // Hit after wind waves
    
    timers.push(hitTimer);

    // Complete animation
    const completeTimer = setTimeout(() => {
      // Reset player animation
      if (playerEl) {
        playerEl.style.animation = '';
      }
      
      // Call onComplete callback
      onComplete();
    }, 2000);
    
    timers.push(completeTimer);

    // Cleanup function
    return () => {
      // Clear all timers
      timers.forEach(timer => clearTimeout(timer));
      
      // Reset animations
      if (playerEl) {
        playerEl.style.animation = '';
      }
      if (opponentEl) {
        opponentEl.style.animation = '';
      }
      
      // Remove all elements
      elements.forEach(element => {
        if (element && element.isConnected) {
          element.remove();
        }
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

IcyWindAnimation.moveType = "ice";
